import {
  ABANDON,
  PASS_THROUGH,
  REFRESH,
  REPLAY,
  RETRY_FLAG,
} from './constants'

const HEADER_NAME = 'Authorization'
const UNAUTHORIZED = 401


/**
 * Decides what a failed request has earned: nothing, a replay, a refresh
 * followed by a replay, or the end of the session.
 *
 * This sits apart from the interceptor so that the whole decision reads in one
 * place, and so that exercising it needs no client, no store and no refresher
 * standing by.
 *
 * @param {Object} args
 * @param {Object} [args.config] - the failed request's Axios config
 * @param {Object} [args.config.headers] - the headers it went out carrying
 * @param {String} [args.config.headers.Authorization]
 * @param {Boolean} [args.config.isTokenRefreshRetry] - set when the failure is
 *   itself a replay of an earlier one
 * @param {String} [args.heldToken] - the access token held right now
 * @param {Object} [args.response] - the server's answer, absent when the
 *   request never earned one
 * @param {Number} [args.response.status]
 *
 * @returns {String} one of `ABANDON`, `PASS_THROUGH`, `REFRESH` or `REPLAY`
 */
export default function chooseRecovery({ config, heldToken, response }) {
  if ( response?.status !== UNAUTHORIZED ) {
    return PASS_THROUGH
  }

  // Only a request carrying a bearer token is recoverable this way, so a 401
  // arriving while none is held keeps whatever meaning it already had. That
  // matters while sessions and bearer tokens coexist: a request authenticated
  // by session cookie must not be answered with a refresh and a trip to the
  // login page.
  if ( !heldToken ) {
    return PASS_THROUGH
  }

  // There is nothing to replay. A 401 is an answer from the server, so it
  // always arrives with the config of the request that earned it, and this
  // cannot happen in practice. It is answered anyway because the alternative
  // is worse than giving up: an absent config spreads into a bare request
  // against the client's own base URL, whose body would then be handed to the
  // caller as though it answered them.
  if ( !config ) {
    return PASS_THROUGH
  }

  // A replay that comes back unauthorized means refreshing did not help, so
  // the session really is over.
  if ( config[RETRY_FLAG] ) {
    return ABANDON
  }

  // The token this request carried has already been replaced since it went
  // out, so it failed against a credential that is gone either way. Replaying
  // is the entire remedy, and refreshing again would spend a second rotation
  // reaching the token already in hand.
  //
  // A request that carried no bearer at all says the same thing: one is held
  // now and was not when it left, so it arrived while the request was out.
  if ( carriesAStaleToken(config, heldToken) ) {
    return REPLAY
  }

  return REFRESH
}

// Axios reports a request's headers back under the same key `bearerInterceptor`
// wrote, verified against Axios 1.16. Were that ever to change, this would read
// `undefined`, always choose a refresh, and behave as it did before the
// comparison existed.
//
function carriesAStaleToken({ headers }, heldToken) {
  return headers?.[HEADER_NAME] !== `Bearer ${ heldToken }`
}
